import axios, { AxiosInstance } from 'axios';

import { applyApiHostRewrite } from '@/shared/api/dev';
import { config } from '@/shared/config';

import type {
	CreatePaymentIntentResponse,
	CreateQuotePayload,
	CreateQuoteResponse,
	EstimateResponse,
	EstimateVehiclesResponse,
	QuickQuoteSettings
} from '@/entities/quickQuote/lib/booking-api.types';

const QUICK_QUOTE_BASE = '/unum/quick-quote';

type QuickQuoteErrorResponse = {
	code?: string;
};

let bookingApi: AxiosInstance | undefined;

const getBookingApi = (): AxiosInstance => {
	if (bookingApi) return bookingApi;

	bookingApi = axios.create({ baseURL: config().getBaseURL() });

	if (process.env.NODE_ENV === 'development') {
		applyApiHostRewrite(bookingApi);
	}

	return bookingApi;
};

// The booking page is public: the only credential is the apiKey from the
// route, which the backend resolves to a shop. It must ride along on every
// request as a Bearer token.
const authConfig = (apiKey: string) => ({
	headers: { authorization: `Bearer ${apiKey}` }
});

const requestConfig = (apiKey: string, signal?: AbortSignal) => ({
	...authConfig(apiKey),
	signal
});

export const getEstimateErrorCode = (error: unknown) => {
	if (!axios.isAxiosError<QuickQuoteErrorResponse>(error)) return undefined;
	return error.response?.data?.code;
};

export const getQuickQuoteSettings = async (
	apiKey: string
): Promise<QuickQuoteSettings> => {
	const response = await getBookingApi().get<QuickQuoteSettings>(
		`${QUICK_QUOTE_BASE}/settings`,
		authConfig(apiKey)
	);

	return response.data;
};

export const createEstimate = async (
	apiKey: string,
	payload:
		| {
				vin: string;
				country?: string;
				kind?: string;
				phone?: string;
				phone_verification_code?: string;
		  }
		| {
				plate: string;
				state: string;
				country?: string;
				kind?: string;
				phone?: string;
				phone_verification_code?: string;
		  },
	signal?: AbortSignal
): Promise<EstimateResponse> => {
	const response = await getBookingApi().post<EstimateResponse>(
		`${QUICK_QUOTE_BASE}/quote/estimate`,
		{
			...payload,
			country: payload.country ?? 'US',
			kind: payload.kind ?? 'Windshield'
		},
		requestConfig(apiKey, signal)
	);

	return response.data;
};

export const createEstimateVehicles = async (
	apiKey: string,
	payload:
		| {
				vin: string;
				country?: string;
		  }
		| {
				plate: string;
				state: string;
				country?: string;
		  },
	signal?: AbortSignal
): Promise<EstimateVehiclesResponse> => {
	const response = await getBookingApi().post<EstimateVehiclesResponse>(
		`${QUICK_QUOTE_BASE}/quote/vehicles`,
		{
			...payload,
			country: payload.country ?? 'US'
		},
		requestConfig(apiKey, signal)
	);

	return response.data;
};

export const confirmPhone = async (
	apiKey: string,
	payload: { phone: string }
): Promise<void> => {
	await getBookingApi().post(
		`${QUICK_QUOTE_BASE}/quote/confirm-phone`,
		payload,
		authConfig(apiKey)
	);
};

export const createPaymentIntent = async (
	apiKey: string,
	payload: { estimateId: string; amount: number }
): Promise<CreatePaymentIntentResponse> => {
	const response = await getBookingApi().post<CreatePaymentIntentResponse>(
		`${QUICK_QUOTE_BASE}/payment/create`,
		payload,
		authConfig(apiKey)
	);

	return response.data;
};

export const createQuote = async (
	apiKey: string,
	payload: CreateQuotePayload
): Promise<CreateQuoteResponse> => {
	const response = await getBookingApi().post<CreateQuoteResponse>(
		`${QUICK_QUOTE_BASE}/quote/create`,
		payload,
		authConfig(apiKey)
	);

	return response.data;
};
